import type { ComponentProps, JSX } from 'react'

import type { ErrorLike } from '@apollo/client'

import { CriticalIcon, EmptyState, Text } from '@buffer-mono/popcorn'

import { AccessDenied } from '~publish/components/AccessDenied'

/**
 * The backend (`findUpdates`) throws this exact message when the actor lacks
 * `readUpdates` access to one of the requested channels. Matched verbatim —
 * keep in sync with
 * `domains/updates-domain/src/use-cases/fetch/findUpdates.ts`.
 */
const NO_CHANNEL_ACCESS_ERROR = 'Actor can not access the specified channels.'

/**
 * Shared error state for the post lists — the All Channels {@link PostList},
 * the single-channel `QueueList`, and the Calendar `CalendarUndatedDraftsSidebar`.
 * Renders the shared {@link AccessDenied} state when the failure is the
 * channel-permission error, and the generic "let our team know" state for
 * anything else. These surfaces query the same `findUpdates` use case, so they
 * must handle its permission error identically.
 */
export const PostListError = ({
  error,
  size = 'large',
}: {
  error: ErrorLike
  size?: ComponentProps<typeof EmptyState>['size']
}): JSX.Element => {
  // When the user has not been granted access to the channel.
  const isPermissionError = error.message.includes(NO_CHANNEL_ACCESS_ERROR)

  if (isPermissionError) {
    return (
      <AccessDenied description="You can not access the specified channel. Try reaching out to your organization owner to make sure you have access to this channel." />
    )
  }

  return (
    <EmptyState variant="critical" size={size}>
      <EmptyState.Icon>
        <CriticalIcon />
      </EmptyState.Icon>
      <EmptyState.Heading>Error happened, let our team know</EmptyState.Heading>
      <EmptyState.Description>
        Please let our team know about it, we&apos;ll fix it as soon as
        possible. <Text color="critical">{error.message}</Text>
      </EmptyState.Description>
    </EmptyState>
  )
}
